import * as Sentry from '@sentry/react';

import '../global';
import { beforeSend } from './beforeSend';

/** Idempotent: initializes Sentry once, but sets the user on every call. */
export function initSentry(tenantName: string, user?: { userId: string; email: string }): void {
    // Checkly's browser checks log into production every 6 hours and accept cookies; Checkly reports their failures.
    if (navigator.webdriver) {
        return;
    }
    if (!Sentry.getClient()) {
        Sentry.init({
            dsn: window.SENTRY_DSN,
            release: window.SENTRY_RELEASE,
            transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
            enableLogs: true,
            enableMetrics: true,
            beforeSend,
            ignoreErrors: [
                // MagicBell client errors, outside our control: https://luminovo.sentry.io/issues/5291827561/
                /^MagicBellError/,
            ],
            // Browser extensions' exceptions reach `window.onerror` as if they were ours; none is actionable.
            denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-web-extension:\/\//],
            initialScope: (scope) => {
                // Tags reach errors and attributes reach logs and metrics, so the tenant goes on both.
                scope.setAttributes({ tenant: tenantName });
                return scope.setTags({
                    'luminovo.version': window.LUMINOVO_VERSION,
                    'screen.width': window.screen.width,
                    'screen.height': window.screen.height,
                    'screen.orientation.type': window.screen.orientation.type,
                    'screen.orientation.angle': window.screen.orientation.angle,
                    tenant: tenantName,
                });
            },
            integrations: [
                Sentry.extraErrorDataIntegration({ depth: 5 }),
                Sentry.browserTracingIntegration({
                    shouldCreateSpanForRequest: (url) => {
                        return !url.match(/(?:magicbell\.com|ably\.io|segment\.(?:io|com))/);
                    },
                    // Long-frame spans were 10% of all spans. Without LoAF the SDK tracks long tasks; turn off both.
                    enableLongAnimationFrame: false,
                    enableLongTask: false,
                }),
                Sentry.browserProfilingIntegration(),
                Sentry.replayIntegration({
                    minReplayDuration: 10 * 1000,
                    blockAllMedia: false,
                    maskAllText: false,
                    maskAllInputs: false,
                    networkDetailAllowUrls: [`https://${window.location.hostname}/api/`],
                    // GET /3rdparty/*/credentials returns the stored supplier API keys, passwords
                    // and client secrets in clear text, and PATCH sends them back, so capturing
                    // bodies for everything under /api/ recorded all of them into the replay. Deny
                    // is evaluated after allow, so every other endpoint keeps its bodies. A pattern
                    // matches by containment, so it also drops the bodies of the digikey auth_code
                    // and pcb-manufacturer calls; URLs are kept. INT-2201. The others carry the
                    // Luminovo API tokens, the customer portal's Stripe keys and a payment's client
                    // secret, a login link to the webhook portal, and the per-user keys that prove who
                    // the user is to MagicBell and Intercom.
                    networkDetailDenyUrls: [
                        '/3rdparty/',
                        '/machine-tokens',
                        '/payments/credentials',
                        '/orders/initialize-payment',
                        '/webhooks/dashboard-url',
                        '/my/magicbell',
                        '/my/intercom',
                    ],
                }),
                Sentry.browserSessionIntegration(),
            ],
            // One span per loaded script, stylesheet and image: a third of our spans. `ignoreResourceSpans` matches
            // exact ops, and there is one per initiator type, so a pattern catches them all. Redirect spans are
            // empty 0 ms markers; redirect detection stays on so a redirect doesn't start a trace of its own.
            ignoreSpans: [{ op: /^resource\./ }, { op: 'navigation.redirect' }],
            // At 0.2 the span quota ran out 6 days into the month (SOU-5740), and at 0.05 it still ran out (SOU-5879).
            tracesSampleRate: 0.01,
            profilesSampleRate: 0,
            // Recording every session used up the replay quota 12 days into the month. Sessions with an error are
            // still all recorded.
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            sendDefaultPii: true,
        });
    }

    if (user) {
        Sentry.setUser({ userId: user.userId, email: user.email });
    }
}
